import classNames from 'classnames';
import { useScrollIntoView } from 'components/shared/DragAndDropWorkspace/TabsWorkspace/Tab/useScrollIntoView';
import { FC, useCallback, useState } from 'react';
import type { WorkspaceType } from 'state/workspaces/model';
import type { WorkspaceTab } from 'state/workspaces/tabs/tabs.model';
import { TabLoadingStatus } from 'state/workspaces/tabs/tabs.model';
import { CustomIconSpinner } from 'utils/customSpinner';

import { useLocalizedTabName } from '../../../LegacyWorkspaceTabs/WorkspaceTabDropdownMenuItem/useLocalizedTabName';
import { TabNameEditor, TabNameEditorHint } from './TabNameEditor';

interface TabProps {
  workspaceType: WorkspaceType;
  isSelected: boolean;
  isBackground: boolean;
  tab: WorkspaceTab;
  onTabSelected: (tab: WorkspaceTab) => void;
  onTabNameChanged?: (tab: WorkspaceTab, newName: string) => void;
  onClose?: (tab: WorkspaceTab) => void;
}

export const Tab: FC<TabProps> = ({
  tab,
  isSelected,
  isBackground,
  onClose,
  onTabNameChanged,
  onTabSelected,
  workspaceType,
}) => {
  const isPhoneMode = (window.visualViewport?.width ?? 1000) < 992;
  const myRef = useScrollIntoView<HTMLLIElement>(isSelected && !isPhoneMode, {
    behavior: 'smooth',
    block: 'end',
    inline: 'center',
  });
  const [isRenaming, setIsRenaming] = useState<boolean>(false);
  const onClick = useCallback(() => {
    onTabSelected(tab);
  }, [onTabSelected, tab]);
  const onCloseClicked = useCallback(() => {
    if (onClose !== undefined) {
      onClose(tab);
    }
  }, [onClose, tab]);
  const onEditName = useCallback(() => setIsRenaming(true), []);
  const onTabRenamed = useCallback(
    (value: string | undefined) => {
      setIsRenaming(false);
      if (onTabNameChanged !== undefined) {
        const text = value?.trim() ?? '';
        if (text.length > 0) {
          onTabNameChanged(tab, text);
        }
      }
    },
    [onTabNameChanged, tab],
  );
  const onCancelRenaming = useCallback(() => setIsRenaming(false), []);
  const tabName = useLocalizedTabName({ customizedName: tab.name, tabIndex: tab.count, workspaceType });

  const { isFrozen, isBusy, loadingStatus } = tab;
  const isLoading = loadingStatus === TabLoadingStatus.Loading;
  const isBroken = loadingStatus === TabLoadingStatus.LoadingFailed;
  return (
    <li
      role="tab"
      ref={myRef}
      className={classNames('nav-item quote-tabs-tab-component h-100', {
        'border-bottom': !isSelected,
      })}
    >
      <a
        className={classNames('nav-link text-nowrap d-flex align-items-center h-100', {
          active: isSelected,
          'text-warning': isBusy,
          'cursor-wait': isLoading,
          'text-muted': isBroken,
        })}
        onDoubleClick={onEditName}
        data-e2e={isSelected ? 'active-quote-tab' : undefined}
      >
        <span onClick={isSelected ? undefined : onClick}>
          {isBroken && (
            <em className="icon icon-sm text-danger pe-1" title="Failed to load this tab">
              error_outline
            </em>
          )}
          {isLoading && <CustomIconSpinner size="sm" className="me-1" color="text-success" />}
          {!isRenaming && (
            <span data-e2e="tab-title" className="px-1">
              {tabName}
            </span>
          )}
          {isFrozen && <em className="icon icon-xs text-muted">lock</em>}
          {isRenaming && (
            <TabNameEditor onTabRenamed={onTabRenamed} onCancelRenaming={onCancelRenaming} name={tab.name}>
              <TabNameEditorHint />
            </TabNameEditor>
          )}
        </span>
        {onClose !== undefined && !isRenaming && (
          <button
            type="button"
            className="btn text-secondary btn-flat btn-icon btn-sm"
            data-e2e="remove-tab-button"
            disabled={isBackground}
          >
            <i className="icon icon-sm" onClick={onCloseClicked}>
              clear
            </i>
          </button>
        )}
      </a>
    </li>
  );
};
